import React, { useEffect, useRef, useState } from "react"
import { Formik } from "formik"
import styles from "../styles/scss/templates/proboardSignUp.module.scss"
import ProboardThankYou from "./proboardThankYou"
import { signUp } from "../services/email"
import {
    TextInput,
    Select,
    ErrorSummary,
    FormStatus,
    SubmitButton,
    RequiredNote,
    bind,
    summaryErrors,
    phoneError,
    isValidEmail,
    formStyles as f,
} from "./form"

const EMPLOYMENT = ["Automotive", "CST", "MRO", "Farming", "DIY"]
// Prefixed ids: this dialog renders on a page that may have other forms
const IDS = {
    firstName: "proboard-firstName",
    lastName: "proboard-lastName",
    email: "proboard-email",
    mobileNumber: "proboard-mobileNumber",
    employee: "proboard-employee",
}

// Wraps Tab / Shift+Tab at the ends of the dialog so focus can't reach the
// page behind it (aria-modal tells screen readers that page is inert)
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const trapTab = (e, dialog) => {
    if (!dialog) return
    const items = [...dialog.querySelectorAll(FOCUSABLE)].filter(el => el.getClientRects().length)
    if (!items.length) return
    const first = items[0]
    const last = items[items.length - 1]
    const active = document.activeElement
    if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault()
        last.focus()
    } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
        e.preventDefault()
        first.focus()
    }
}

const ProboardSignUp = ({ showModal, setShowModal }) => {
    const [thankYouModal, setThankYouModal] = useState(false)
    const [submitError, setSubmitError] = useState(false)
    const dialogRef = useRef(null)
    const open = showModal && !thankYouModal

    // The sign-up dialog and then its thank-you dialog form one modal session
    const active = open || thankYouModal
    const openerRef = useRef(null)

    // Dialog basics: focus moves into it on open, Tab stays inside it, Escape
    // closes it, and focus goes back to whatever opened it ("Join Today")
    // once the session ends (after the thank-you dialog, on success)
    useEffect(() => {
        if (!active) return
        if (!openerRef.current) openerRef.current = document.activeElement
        // The thank-you dialog focuses its own close button (autoFocus)
        if (open && dialogRef.current) dialogRef.current.focus()
        // Capture phase, so this runs before the dropdown closes itself: Escape
        // on an open dropdown should close only the dropdown, not the dialog
        const onKey = e => {
            if (e.key === "Tab") return trapTab(e, document.querySelector('[role="dialog"][aria-modal="true"]'))
            if (e.key !== "Escape") return
            const t = e.target
            if (t && t.getAttribute && t.getAttribute("role") === "combobox" && t.getAttribute("aria-expanded") === "true") return
            if (thankYouModal) setThankYouModal(false)
            else setShowModal(false)
        }
        document.addEventListener("keydown", onKey, true)
        return () => document.removeEventListener("keydown", onKey, true)
    }, [active, open, thankYouModal, setShowModal])

    // This component stays mounted while closed, so a failed send's message
    // would otherwise still be there (and grab focus) on the next open
    useEffect(() => {
        if (!open) setSubmitError(false)
    }, [open])

    useEffect(() => {
        const opener = openerRef.current
        if (active || !opener) return
        openerRef.current = null
        if (opener.focus && document.body.contains(opener)) opener.focus()
    }, [active])

    return (
        <>
            {open ? (
                <div
                    className={styles.container}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="proboard-signup-title"
                    tabIndex={-1}
                    ref={dialogRef}
                >
                    <div className={styles.header}>
                        <h2 id="proboard-signup-title" className={styles.headerTitle}>
                            PRO Board Sign Up
                        </h2>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={() => setShowModal(false)}
                            className={styles.closeButton}
                        >
                            +
                        </button>
                    </div>

                    <div className={styles.container_text}>
                        <p>
                            WD-40 Company’s PRO Board is looking for new members
                            to provide input on new products and uses, and
                            feedback on how we can help you get the job done
                            right. Sign-up below and a representative will be in
                            touch to see if you meet the criteria for
                            membership.
                        </p>
                    </div>

                    <Formik
                        initialValues={{
                            firstName: "",
                            lastName: "",
                            email: "",
                            mobileNumber: "",
                            employee: "",
                        }}
                        validate={values => {
                            const errors = []
                            if (!values.firstName.trim()) errors.push({ field: "firstName", message: "First Name is required" })
                            if (!values.lastName.trim()) errors.push({ field: "lastName", message: "Last Name is required" })
                            if (!values.email.trim()) errors.push({ field: "email", message: "Email is required" })
                            else if (!isValidEmail(values.email)) errors.push({ field: "email", message: "Invalid email address" })
                            const phoneMsg = phoneError(values.mobileNumber, { required: true, requiredMessage: "Phone is required" })
                            if (phoneMsg) errors.push({ field: "mobileNumber", message: phoneMsg })
                            if (!values.employee) errors.push({ field: "employee", message: "Employment Focus is required" })
                            return errors
                        }}
                        onSubmit={(values, { setSubmitting, resetForm }) => {
                            setSubmitError(false)
                            const data = {
                                firstName: values.firstName,
                                lastName: values.lastName,
                                email: values.email,
                                phone: values.mobileNumber,
                                employeeFocus: values.employee || "Automotive",
                                lists: [37547981],
                                formIdentifier: "proboard",
                            }
                            signUp(data)
                                .then(() => {
                                    setSubmitting(false)
                                    resetForm()
                                    // Thank-you first: these updates aren't batched (promise
                                    // callback), and showModal=false alone would end the
                                    // modal session and send focus back to "Join Today"
                                    setThankYouModal(true)
                                    setShowModal(false)
                                })
                                .catch(err => {
                                    console.log(err)
                                    setSubmitting(false)
                                    setSubmitError(true)
                                })
                        }}
                    >
                        {props => (
                            <form className={`${f.form} ${styles.proboardForm}`} onSubmit={props.handleSubmit} noValidate>
                                <RequiredNote />
                                <ErrorSummary errors={summaryErrors(props, IDS)} submitCount={props.submitCount} />
                                <FormStatus status={submitError ? "error" : null} />
                                <TextInput id={IDS.firstName} label="First Name" required autoComplete="given-name" {...bind(props, "firstName")} />
                                <TextInput id={IDS.lastName} label="Last Name" required autoComplete="family-name" {...bind(props, "lastName")} />
                                <TextInput id={IDS.email} label="Email" type="email" required autoComplete="email" {...bind(props, "email")} />
                                <TextInput id={IDS.mobileNumber} label="Phone" type="tel" mask="phone" required autoComplete="tel-national" placeholder="(000) 000-0000" {...bind(props, "mobileNumber")} />
                                <Select id={IDS.employee} label="Employment Focus" required options={EMPLOYMENT} {...bind(props, "employee")} />
                                <div className={f.actions}>
                                    <SubmitButton busy={props.isSubmitting}>Sign Up</SubmitButton>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            ) : null}

            {thankYouModal ? (
                <ProboardThankYou setShowModal={setThankYouModal} />
            ) : null}
        </>
    )
}

export default ProboardSignUp
